"use client";

import React from "react";
import GenericToggle from "@/shared/ui/controls/GenericToggle";
import GenericEditable from "@/shared/ui/controls/GenericEditable";
import type {
  PrintSettings,
  SupportedLanguage,
  MindstringPrototype,
} from "@/generated/fastapi";

type RelevantConfig = ReturnType<
  typeof import("@/features/profile").getRelevantPrintSettingsConfig
>;

// ---------------------------------------------
// Main Component
// ---------------------------------------------

type CustomSectionNaming = {
  printSettings: PrintSettings;
  resetKey: number;
  relevantPersonalDataConfig: RelevantConfig;
  setPrintSettingsAction: React.Dispatch<React.SetStateAction<PrintSettings>>;
  handleKeyDownAction: (e: React.KeyboardEvent, submitFn: () => void) => void;
  setCustomSectionEnabledAction: (
    sectionKey: MindstringPrototype,
    enabled: boolean,
    language?: SupportedLanguage,
  ) => Promise<PrintSettings | null>;
  updateCustomSectionAction: (
    sectionKey: MindstringPrototype,
    patch: Partial<{ text: string | null; enabled: boolean }>,
    language?: SupportedLanguage,
  ) => Promise<PrintSettings | null>;
};

export default function CustomSectionNaming({
  printSettings,
  resetKey,
  relevantPersonalDataConfig,
  setPrintSettingsAction,
  handleKeyDownAction,
  setCustomSectionEnabledAction,
  updateCustomSectionAction,
}: CustomSectionNaming) {
  return (
    <>
      {/* Job activity */}
      <GenericToggle
        key={`job-activity-naming-${resetKey}`}
        label="Job Activity Naming"
        description="Custom naming for Job Activity section."
        value={relevantPersonalDataConfig.job_activity_naming.enabled || false}
        onChange={(enabled) =>
          void setCustomSectionEnabledAction(
            "job_activity",
            enabled,
            printSettings.print_language,
          )
        }
        minW="48"
      />

      {relevantPersonalDataConfig.job_activity_naming.enabled && (
        <GenericEditable
          key={`job-activity-${resetKey}`}
          label="Job Activity"
          placeholder="Custom naming for Job Activity section"
          value={
            (printSettings.custom_section_names?.[printSettings.print_language]
              ?.job_activity?.text as string) || ""
          }
          onChange={(text) =>
            setPrintSettingsAction((prev) => ({
              ...prev,
              custom_section_names: {
                ...prev.custom_section_names,
                [prev.print_language]: {
                  ...prev.custom_section_names[prev.print_language],
                  job_activity: {
                    ...relevantPersonalDataConfig.job_activity_naming,
                    text: text || null,
                  },
                },
              },
            }))
          }
          onSubmit={() =>
            void updateCustomSectionAction(
              "job_activity",
              {
                text:
                  printSettings.custom_section_names?.[
                    printSettings.print_language
                  ]?.job_activity?.text || null,
              },
              printSettings.print_language,
            )
          }
          onClear={() =>
            void updateCustomSectionAction(
              "job_activity",
              { text: null },
              printSettings.print_language,
            )
          }
          handleKeyDown={handleKeyDownAction}
          disabled={!relevantPersonalDataConfig.job_activity_naming.enabled}
        />
      )}

      {/* Learning outcome */}
      <GenericToggle
        key={`learning-outcome-naming-${resetKey}`}
        label="Learning Outcome Naming"
        description="Custom naming for Learning Outcome section."
        value={
          relevantPersonalDataConfig.learning_outcome_naming.enabled || false
        }
        onChange={(enabled) =>
          void setCustomSectionEnabledAction(
            "learning_outcome",
            enabled,
            printSettings.print_language,
          )
        }
        minW="48"
      />

      {relevantPersonalDataConfig.learning_outcome_naming.enabled && (
        <GenericEditable
          key={`learning-outcome-${resetKey}`}
          label="Learning Outcome"
          placeholder="Custom naming for Learning Outcome section"
          value={
            (printSettings.custom_section_names?.[printSettings.print_language]
              ?.learning_outcome?.text as string) || ""
          }
          onChange={(text) =>
            setPrintSettingsAction((prev) => ({
              ...prev,
              custom_section_names: {
                ...prev.custom_section_names,
                [prev.print_language]: {
                  ...prev.custom_section_names[prev.print_language],
                  learning_outcome: {
                    ...relevantPersonalDataConfig.learning_outcome_naming,
                    text: text || null,
                  },
                },
              },
            }))
          }
          onSubmit={() =>
            void updateCustomSectionAction(
              "learning_outcome",
              {
                text:
                  printSettings.custom_section_names?.[
                    printSettings.print_language
                  ]?.learning_outcome?.text || null,
              },
              printSettings.print_language,
            )
          }
          onClear={() =>
            void updateCustomSectionAction(
              "learning_outcome",
              { text: null },
              printSettings.print_language,
            )
          }
          handleKeyDown={handleKeyDownAction}
          disabled={!relevantPersonalDataConfig.learning_outcome_naming.enabled}
        />
      )}

      {/* Workplace characteristic */}
      <GenericToggle
        key={`workplace-characteristic-naming-${resetKey}`}
        label="Workplace Characteristic Naming"
        description="Custom naming for Workplace Characteristic section."
        value={
          relevantPersonalDataConfig.workplace_characteristic_naming.enabled ||
          false
        }
        onChange={(enabled) =>
          void setCustomSectionEnabledAction(
            "workplace_characteristic",
            enabled,
            printSettings.print_language,
          )
        }
        minW="48"
      />

      {relevantPersonalDataConfig.workplace_characteristic_naming.enabled && (
        <GenericEditable
          key={`workplace-characteristic-${resetKey}`}
          label="Workplace Characteristic"
          placeholder="Custom naming for Workplace Characteristic"
          value={
            (printSettings.custom_section_names?.[printSettings.print_language]
              ?.workplace_characteristic?.text as string) || ""
          }
          onChange={(text) =>
            setPrintSettingsAction((prev) => ({
              ...prev,
              custom_section_names: {
                ...prev.custom_section_names,
                [prev.print_language]: {
                  ...prev.custom_section_names[prev.print_language],
                  workplace_characteristic: {
                    ...relevantPersonalDataConfig.workplace_characteristic_naming,
                    text: text || null,
                  },
                },
              },
            }))
          }
          onSubmit={() =>
            void updateCustomSectionAction(
              "workplace_characteristic",
              {
                text:
                  printSettings.custom_section_names?.[
                    printSettings.print_language
                  ]?.workplace_characteristic?.text || null,
              },
              printSettings.print_language,
            )
          }
          onClear={() =>
            void updateCustomSectionAction(
              "workplace_characteristic",
              { text: null },
              printSettings.print_language,
            )
          }
          handleKeyDown={handleKeyDownAction}
          disabled={
            !relevantPersonalDataConfig.workplace_characteristic_naming.enabled
          }
        />
      )}

      {/* Individual description */}
      <GenericToggle
        key={`individual-description-naming-${resetKey}`}
        label="Individual Description Naming"
        description="Custom naming for Individual Description section."
        value={
          relevantPersonalDataConfig.individual_description_naming.enabled ||
          false
        }
        onChange={(enabled) =>
          void setCustomSectionEnabledAction(
            "individual_description",
            enabled,
            printSettings.print_language,
          )
        }
        minW="48"
      />

      {relevantPersonalDataConfig.individual_description_naming.enabled && (
        <GenericEditable
          key={`individual-description-${resetKey}`}
          label="Individual Description"
          placeholder="Custom naming for Individual Description"
          value={
            (printSettings.custom_section_names?.[printSettings.print_language]
              ?.individual_description?.text as string) || ""
          }
          onChange={(text) =>
            setPrintSettingsAction((prev) => ({
              ...prev,
              custom_section_names: {
                ...prev.custom_section_names,
                [prev.print_language]: {
                  ...prev.custom_section_names[prev.print_language],
                  individual_description: {
                    ...relevantPersonalDataConfig.individual_description_naming,
                    text: text || null,
                  },
                },
              },
            }))
          }
          onSubmit={() =>
            void updateCustomSectionAction(
              "individual_description",
              {
                text:
                  printSettings.custom_section_names?.[
                    printSettings.print_language
                  ]?.individual_description?.text || null,
              },
              printSettings.print_language,
            )
          }
          onClear={() =>
            void updateCustomSectionAction(
              "individual_description",
              { text: null },
              printSettings.print_language,
            )
          }
          handleKeyDown={handleKeyDownAction}
          disabled={
            !relevantPersonalDataConfig.individual_description_naming.enabled
          }
        />
      )}
    </>
  );
}
